import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import * as React from "react";

export const H1 = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>): React.ReactElement => {
  return (
    <h1
      className={cn(
        "scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl",
        className,
      )}
      {...props}
    />
  );
};

export const H2 = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>): React.ReactElement => {
  return (
    <h2
      className={cn(
        "scroll-m-20 text-xl font-medium tracking-tight",
        className,
      )}
      {...props}
    />
  );
};

export const H3 = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>): React.ReactElement => {
  return (
    <h3
      className={cn(
        "scroll-m-20 text-2xl font-semibold tracking-tight",
        className,
      )}
      {...props}
    />
  );
};

export const H4 = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>): React.ReactElement => {
  return (
    <h4
      className={cn("scroll-m-20 text-lg tracking-tight", className)}
      {...props}
    />
  );
};

export const Paragraph = ({
  className,
  variant = "normal",
  ...props
}: React.HTMLAttributes<HTMLParagraphElement> & {
  variant?: "normal" | "muted";
}): React.ReactElement => {
  return (
    <p
      className={cn(
        "leading-5",
        variant === "muted" && "text-sm text-muted-foreground font-light",
        className,
      )}
      {...props}
    >
      {props.children}
    </p>
  );
};

interface TypographyTableProps {
  data: Record<string, any>[];
  headers?: string[];
  showHeaders?: boolean;
  className?: string;
  zebraStriping?: boolean;
  compact?: boolean;
  borderless?: boolean;
  aligns?: ("left" | "center" | "right")[];
  widths?: string[];
  loading?: boolean;
  heavyRightColumns?: boolean;
}

// Helper function to get width class
// Without this function, Tailwind won't pick up dynamic classes
// for reasons I don't fully understand.
const getWidthClass = (w: string) => {
  const widthMap: Record<string, string> = {
    "1/12": "w-1/12",
    "2/12": "w-2/12",
    "3/12": "w-3/12",
    "4/12": "w-4/12",
    "5/12": "w-5/12",
    "6/12": "w-6/12",
    "7/12": "w-7/12",
    "8/12": "w-8/12",
    "9/12": "w-9/12",
    "10/12": "w-10/12",
    "11/12": "w-11/12",
    full: "w-full",
    auto: "w-auto",
  };
  return widthMap[w] || `w-${w}`;
};

// Simple table cell component
const TableCell = ({
  content,
  align,
  compact,
  width,
  light,
}: {
  content: React.ReactNode;
  align: "left" | "center" | "right";
  compact?: boolean;
  width: string;
  light?: boolean;
}) => {
  const cellClasses = cn(
    "align-middle",
    compact ? "px-1 py-0.5 text-sm" : "px-4 py-2",
    "text-left [&[align=center]]:text-center [&[align=right]]:text-right",
    getWidthClass(width),
    width === "full" ? "break-all" : "break-words",
    light && "font-light",
  );

  return (
    <td align={align} className={cellClasses}>
      <div
        className={cn(
          "flex items-center",
          align === "center" && "justify-center",
          align === "right" && "justify-end",
          align === "left" && "justify-start",
          "min-h-1",
        )}
      >
        {content}
      </div>
    </td>
  );
};

export const TypographyTable = ({
  data,
  headers,
  showHeaders = true,
  className,
  compact = false,
  borderless = false,
  aligns,
  widths,
  loading = false,
  zebraStriping = false,
  heavyRightColumns = false,
}: TypographyTableProps): React.ReactElement => {
  const tableHeaders = headers || Object.keys(data[0] || {});
  const numColumns = tableHeaders.length;

  // Convert headers to sentence case
  const formatHeader = (header: string): string => {
    return header.charAt(0).toUpperCase() + header.slice(1).toLowerCase();
  };

  if (aligns && aligns.length !== numColumns) {
    throw new Error("Aligns array length must match number of columns");
  }

  if (widths && widths.length !== numColumns) {
    throw new Error("Widths array length must match number of columns");
  }

  return (
    <div className={className ?? ""}>
      <div className={borderless ? "" : "rounded-lg bg-background border"}>
        <table className="w-full table-auto">
          {showHeaders && (
            <thead>
              <tr
                className={cn(
                  "m-0 p-0",
                  zebraStriping && "even:bg-muted/80",
                  !borderless && "border-b border-border",
                )}
              >
                {tableHeaders.map((header, headerIndex) => (
                  <th
                    key={header}
                    className={cn(
                      "font-bold",
                      "text-left [&[align=center]]:text-center [&[align=right]]:text-right",
                      compact ? "px-1 py-0.5 text-sm" : "px-4 py-2",
                      getWidthClass(
                        widths?.[headerIndex] ||
                          (headerIndex === 0 ? "auto" : "full"),
                      ),
                    )}
                    align={
                      aligns
                        ? aligns[headerIndex]
                        : headerIndex === 0
                          ? "left"
                          : "right"
                    }
                  >
                    {formatHeader(header)}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {data.map((row, index) => (
              <tr
                key={index}
                className={cn(
                  "m-0 p-0",
                  index !== data.length - 1 &&
                    !borderless &&
                    "border-b border-border",
                  zebraStriping && "even:bg-muted/80",
                )}
              >
                {tableHeaders.map((header, column) => {
                  const rawContent = row[header];
                  // Handle different content types properly
                  let displayContent;
                  if (rawContent === null || rawContent === undefined) {
                    displayContent = "—";
                  } else if (typeof rawContent === "string") {
                    displayContent = rawContent;
                  } else if (
                    typeof rawContent === "number" ||
                    typeof rawContent === "boolean"
                  ) {
                    displayContent = String(rawContent);
                  } else if (React.isValidElement(rawContent)) {
                    // Handle React elements (like links)
                    displayContent = rawContent;
                  } else {
                    // Handle other objects
                    displayContent = JSON.stringify(rawContent);
                  }
                  return (
                    <TableCell
                      key={header}
                      content={
                        loading ? (
                          <Skeleton className="h-4 w-full" />
                        ) : (
                          displayContent
                        )
                      }
                      align={
                        aligns
                          ? aligns[column]
                          : column === 0
                            ? "left"
                            : "right"
                      }
                      compact={compact}
                      width={
                        widths?.[column] || (column === 0 ? "auto" : "full")
                      }
                      light={!heavyRightColumns && column > 0}
                    />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const EquationRule = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>): React.ReactElement => {
  return (
    <div
      className={cn("border-b-2 border-foreground mx-0 my-2", className)}
      {...props}
    />
  );
};
